import React, { useState } from "react";
import dynamic from "next/dynamic";

import { Formik, Field, Form } from "formik";
import { object, string, number } from "yup";
import { FormattedNumberInput, Input } from "../components/Input";

import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Checkbox,
  Flex,
} from "@chakra-ui/react";
import Loader from "../components/Loader";
import useDonation from "../hooks/useDonations";
import { formatMoney, getMaxTip, getMinTip, VENDOR_CUT, VENDOR_CUT_RATES, getMinTipIDR, getMaxTipIDR, getMinTipPHP, getMaxTipPHP, getMinTipJPY, getMaxTipJPY } from "../utils/";
import { Large, P } from "../components/Type";
import Link from "../components/Link";
import getVideoId from "get-video-id";
import { UsernameBadge } from "../components/Username";
import isEmail from "validator/lib/isEmail";
import { Trans, msg, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { PaymentMethods } from "./PaymentMethods";
import { getFormError } from "../i18n";
import { SoundboardSelector } from "./StreamerPage/MediaSelector/SoundboardSelector";
import { PaymentMethodSelectorProvider, usePaymentMethodSelector } from "../contexts/paymentMethodSelector";
import Button from "../components/Button";
import { useRouter } from "next/router";

const baseYupModel = {
  from: string()
    .required(msg`Nama tidak boleh kosong`)
    .max(32, msg`Nama terlalu panjang`)
    .test(
      "not-readable-domain",
      (message) => msg`Nama tidak diperbolehkan`,
      async (value = "") => {
        return await !isReadableAsDomain(value);
      }
    ),

  message: string()
    .notRequired(msg`Pesan tidak boleh kosong`)
    .max(140),
  ytStart: number().moreThan(-1, msg`Tidak boleh negatif`),
  email: string()
    .required(msg`Masukkan email kamu`)
    .email(msg`Masukan email kamu`)
    .test(
      "is-valid",
      (message) => msg`Periksa lagi email kamu`,
      async (value = "") => {
        return await isEmail(value);
      }
    ),
  phone: string(),
};

const getYupSchema = (currency) => {
  const MINIMUM_TIP_AMOUNT = getMinTip(currency);
  const MAXIMUM_TIP_AMOUNT = getMaxTip(currency);

  return object().shape({
    ...baseYupModel,
    amount: number()
      .required(msg`Masukkan nominal yang ingin diberikan`)
      .min(
        MINIMUM_TIP_AMOUNT,
        msg`Nominal minimum ${formatMoney(MINIMUM_TIP_AMOUNT, {
          currency,
        })}`
      )
      .max(
        MAXIMUM_TIP_AMOUNT,
        msg`Nominal maksimum ${formatMoney(MAXIMUM_TIP_AMOUNT, {
          currency,
        })}`
      ),
  });
};

function isReadableAsDomain(str) {
  // Remove all white spaces
  var noSpaces = str.replace(/\s/g, "");

  if (noSpaces.indexOf("http") >= 0 || noSpaces.indexOf(":/") >= 0) {
    return true;
  }

  // Use regex to check for domain name format
  var domainRegex = /^[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}$/;

  return domainRegex.test(noSpaces.toLowerCase());
}

const phoneRegex = new RegExp("^08\\d{7,11}$");

const getGatewayTipRange = (currency, gateway) => {
  switch (currency) {
    case "PHP":
      return { min: getMinTipPHP(gateway), max: getMaxTipPHP(gateway) };
    case "JPY":
      return { min: getMinTipJPY(gateway), max: getMaxTipJPY(gateway) };
    case "IDR":
    default:
      return { min: getMinTipIDR(gateway), max: getMaxTipIDR(gateway) };
  }
};

const isAmountWithinGatewayRange = ({ currency, gateway, amount, transactionFeePolicy }) => {
  if (!gateway) return true;
  const n = Number(amount);
  if (!Number.isFinite(n)) return false;

  const { min, max } = getGatewayTipRange(currency, gateway);
  const rate = VENDOR_CUT_RATES[gateway];
  if (transactionFeePolicy === "TIPPER" && Array.isArray(rate)) {
    return n >= min && n <= (max - rate[1]);
  }
  return n >= min && n <= max;
};

const SoundboardForm = ({
  plus,
  overlay,
  vote,
  streamerId,
  isGopayEnabled,
  isOvoEnabled,
  isDanaEnabled,
  isLinkAjaEnabled,
  isQRISEnabled,
  isBCAVAEnabled,
  isMandiriVAEnabled,
  isBRIVAEnabled,
  isBNIVAEnabled,
  isPermataVAEnabled,
  isBJBVAEnabled,
  isBSIVAEnabled,
  isCIMBVAEnabled,
  currUsername = "",
  currEmail = "",
  appliedFee = false,
  currency,
  transactionFeePolicy,
}) => {
  const { createDonation, calculateAmountToPay } = useDonation();
  const yupSchema = getYupSchema(currency);
  const { soundboard_items } = overlay;
  const { _ } = useLingui();
  const router = useRouter();

  const [selectedSoundboardItem, setSelectedSoundboardItem] = useState({});

  return (
    <PaymentMethodSelectorProvider>
      <Formik
        validationSchema={yupSchema}
        initialValues={{
          agree: false,
          notUnderage: false,
          from: currUsername,
          message: "",
          amount: "",
          payment_type: "",
          email: currEmail,
          phone: "",
          vote: "",
          giphy: null,
          yt: "",
          ytStart: 0,
          mediaType: null,
          currency,
          soundboard: null,
          amountToPay: "",
          pgFee: "",
          platformFee: "",
        }}
        enableReinitialize
        validate={(val) => {
          const error = {};

          if (val.payment_type === "ovo") {
            if (!phoneRegex.test(val.phone)) {
              error["phone"] = msg`Format nomor telepon salah. Gunakan nomor lainnya`;
            }
            if (!val.phone) {
              error[
                "phone"
              ] = msg`Masukkan nomor telepon untuk pembayaran ${val.payment_type.toUpperCase()}`;
            }
          }
          if (!val.notUnderage) {
            error["notUnderage"] = msg`Anda harus berusia 17 tahun atau lebih.`;
          }
          if (!val.agree) {
            error[
              "agree"
            ] = msg`Anda harus menyetujui syarat dan ketentuan saweria.co`;
          }
          if (val.mediaType === "sb") {
            const { soundboard } = val;
            if (!soundboard) {
              error["soundboard"] = msg`Soundboard harus dipilih`;
            }
          }

          return error;
        }}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const { amountToPay, pgFee, platformFee, ...valueRes } = values;
            await createDonation(streamerId, valueRes);
          } catch (error) {
            console.log(error);
          }
          setSubmitting(false);
        }}
      >
        {({
          setFieldValue,
          submitForm,
          isSubmitting,
          values,
          setFieldError,
          errors,
        }) => {
          const { _ } = useLingui();

          const onClickPaymentMethod = (type) => async () => {
            setSelectedPaymentMethod(type);
            await setFieldValue("payment_type", type, false);
            const { username } = router.query;
            const amountToPayRes = await calculateAmountToPay(username, {
              ...values,
              payment_type: type,
            });
            await setFieldValue("amountToPay", amountToPayRes.amount_to_pay);
            await setFieldValue("pgFee", amountToPayRes.pg_fee);
            await setFieldValue("platformFee", amountToPayRes.platform_fee);
          };

          const {
            selectedPaymentMethod,
            setSelectedPaymentMethod,
            setTransactionFeePolicy,
            setTransactionAmount,
            setPgFee,
          } = usePaymentMethodSelector();
          setTransactionFeePolicy(transactionFeePolicy);
          setTransactionAmount(values.amount);
          setPgFee(values.pgFee);

          if (isSubmitting) {
            return <Loader />;
          }

          const amountNumber = Number(values.amount);
          const isAmountFilled = values.amount !== "" && Number.isFinite(amountNumber);

          const isCoreFormFilled =
            isAmountFilled &&
            Boolean(values.from?.trim()) &&
            Boolean(values.email?.trim()) &&
            Boolean(values.notUnderage) &&
            Boolean(values.agree);

          const shouldFilterByGatewayRange = isCoreFormFilled;

          const isGatewayOutOfRange = (gateway) =>
            shouldFilterByGatewayRange &&
            !isAmountWithinGatewayRange({
              currency,
              gateway,
              amount: amountNumber,
              transactionFeePolicy,
            });

          const disabledPayments = {
            qris: isGatewayOutOfRange("qris"),
            gopay: isGatewayOutOfRange("gopay"),
            ovo: isGatewayOutOfRange("ovo"),
            dana: isGatewayOutOfRange("dana"),
            linkaja: isGatewayOutOfRange("linkaja"),
            bca: isGatewayOutOfRange("bca"),
            mandiri: isGatewayOutOfRange("mandiri"),
            bri: isGatewayOutOfRange("bri"),
            bni: isGatewayOutOfRange("bni"),
            permata: isGatewayOutOfRange("permata"),
            bjb: isGatewayOutOfRange("bjb"),
            bsi: isGatewayOutOfRange("bsi"),
            cimb: isGatewayOutOfRange("cimb"),
            shopeepay: isGatewayOutOfRange("shopeepay"),
            grabpay: isGatewayOutOfRange("grabpay"),
            gcash: isGatewayOutOfRange("gcash"),
            paymaya: isGatewayOutOfRange("paymaya"),
            paypay: isGatewayOutOfRange("paypay"),
            alipay: isGatewayOutOfRange("alipay"),
            linepay: isGatewayOutOfRange("linepay"),
            rakutenpay: isGatewayOutOfRange("rakutenpay"),
            merpay: isGatewayOutOfRange("merpay"),
            inter_cc: isGatewayOutOfRange("inter_cc"),
            wechat_pay: isGatewayOutOfRange("wechat_pay"),
          };

          const selectedGateway = values.payment_type || selectedPaymentMethod;
          const isSelectedPaymentAmountValid = selectedGateway
            ? !disabledPayments[selectedGateway]
            : true;

          const allowPayment = !Object.keys(errors).length && values.agree && isSelectedPaymentAmountValid;

          return (
            <Form>
              <Field type="text" name="soundboard">
                {({ field, form }) => {
                  return (
                    <FormControl
                      isRequired
                      mb={3}
                      isInvalid={
                        form.errors.soundboard && form.touched.soundboard
                      }
                    >
                      <FormLabel htmlFor="message">
                        <Trans>Pilih Suara:</Trans>
                      </FormLabel>
                      <SoundboardSelector
                        setFieldValue={setFieldValue}
                        values={values}
                        mode={"tipper"}
                        overlay={overlay}
                        currency={currency}
                        soundboardData={soundboard_items}
                        selectedSoundboardItem={selectedSoundboardItem}
                        setSelectedSoundboardItem={setSelectedSoundboardItem}
                        setSelectedPaymentMethod={setSelectedPaymentMethod}
                      />
                      <FormErrorMessage>
                        {getFormError(form.errors.soundboard, _)}
                      </FormErrorMessage>
                    </FormControl>
                  );
                }}
              </Field>

              <Field type="text" name="from">
                {({ field, form }) => {
                  return (
                    <FormControl
                      isRequired
                      isReadOnly={currUsername}
                      mb={3}
                      isInvalid={form.errors.from && form.touched.from}
                    >
                      <FormLabel htmlFor="from">
                        <Trans>Dari:</Trans>
                      </FormLabel>
                      {currUsername ? (
                        <UsernameBadge
                          w={"min-content"}
                          username={currUsername}
                        ></UsernameBadge>
                      ) : (
                        <Input
                          {...field}
                          data-testid="from-input"
                          id="from"
                          placeholder={_(msg`Budi`)}
                        />
                      )}
                      <FormErrorMessage>
                        {getFormError(form.errors.from, _)}
                      </FormErrorMessage>
                    </FormControl>
                  );
                }}
              </Field>

              <Field name="email">
                {({ field, form }) => {
                  return (
                    <FormControl
                      display={currEmail ? "none" : "block"}
                      isRequired
                      isReadOnly={currEmail}
                      mb={3}
                      isInvalid={form.errors.email && form.touched.email}
                    >
                      <FormLabel htmlFor="email"><Trans>Email:</Trans></FormLabel>
                      <Input
                        {...field}
                        data-testid="email-input"
                        type="email"
                        id="email"
                        placeholder={_(msg`budi@saweria.co`)}
                      />
                      <FormErrorMessage>
                        {getFormError(form.errors.email, _)}
                      </FormErrorMessage>
                    </FormControl>
                  );
                }}
              </Field>

              <Field type="text" name="message">
                {({ field, form }) => {
                  return (
                    <FormControl
                      mb={3}
                      isInvalid={form.errors.message && form.touched.message}
                    >
                      <FormLabel htmlFor="message">
                        <Trans>Pesan:</Trans>
                      </FormLabel>
                      <Input
                        {...field}
                        data-testid="message-input"
                        id="message"
                        placeholder={_(msg`Selamat pagi`)}
                      />
                      <FormErrorMessage>
                        {getFormError(form.errors.message, _)}
                      </FormErrorMessage>
                    </FormControl>
                  );
                }}
              </Field>

              {["ovo"].includes(values.payment_type) ? (
                <>
                  <Field name="phone">
                    {({ field, form }) => {
                      return (
                        <FormControl
                          isRequired
                          mb={3}
                          isInvalid={form.errors.phone && form.touched.phone}
                        >
                          <FormLabel htmlFor="phone"><Trans>Nomor telepon:</Trans></FormLabel>
                          <Input
                            {...field}
                            type="tel"
                            data-testid="phone-input"
                            id="phone"
                            placeholder={_(
                              msg`Nomor yang terdaftar di e-wallet pilihan`
                            )}
                          />
                          <FormErrorMessage>
                            {getFormError(form.errors.phone, _)}
                          </FormErrorMessage>
                        </FormControl>
                      );
                    }}
                  </Field>
                </>
              ) : null}
              <Field name="notUnderage">
                {({ field, form }) => {
                  return (
                    <FormControl
                      isRequired
                      mb="3"
                      isInvalid={
                        form.errors.notUnderage && form.touched.notUnderage
                      }
                    >
                      <Checkbox
                        data-testid="underage-checkbox"
                        size="lg"
                        {...field}
                        isInvalid={
                          form.errors.notUnderage && form.touched.notUnderage
                        }
                        borderColor="black"
                      >
                        <P fontSize="0.75em" mb={0}>
                          <Trans>Saya berusia 17 tahun atau lebih</Trans>
                        </P>
                      </Checkbox>

                      <FormErrorMessage>
                        {getFormError(form.errors.notUnderage, _)}
                      </FormErrorMessage>
                    </FormControl>
                  );
                }}
              </Field>

              <Field name="agree">
                {({ field, form }) => {
                  return (
                    <FormControl
                      isRequired
                      mb="3"
                      isInvalid={form.errors.agree && form.touched.agree}
                    >
                      <Checkbox
                        data-testid="agree-checkbox"
                        size="lg"
                        {...field}
                        isInvalid={form.errors.agree && form.touched.agree}
                        borderColor="black"
                      >
                        <P fontSize="0.75em" mb={0}>
                          <Trans>
                            Saya memahami dan menyetujui bahwa dukungan ini
                            saya berikan secara sukarela untuk mendukung
                            kreator, tidak dapat dikembalikan, bukan transaksi
                            komersial, tidak digunakan untuk aktivitas ilegal,
                            serta telah sesuai dengan{" "}
                            <Link textDecoration="underline" href="/terms">
                              {`syarat dan ketentuan`}
                            </Link>{" "}
                            &{" "}
                            <Link textDecoration="underline" href="/privacy">
                              kebijakan privasi
                            </Link>{" "}
                            saweria.co
                          </Trans>
                        </P>
                      </Checkbox>

                      <FormErrorMessage>
                        {getFormError(form.errors.agree, _)}
                      </FormErrorMessage>
                    </FormControl>
                  );
                }}
              </Field>
              <PaymentMethods
                currency={currency}
                isSubmitting={isSubmitting}
                allowPayment={allowPayment}
                enabledPayments={{
                  isDanaEnabled,
                  isQRISEnabled,
                  isGopayEnabled,
                  isLinkAjaEnabled,
                  isOvoEnabled,
                  isBCAVAEnabled,
                  isMandiriVAEnabled,
                  isBRIVAEnabled,
                  isBNIVAEnabled,
                  isPermataVAEnabled,
                  isBJBVAEnabled,
                  isBSIVAEnabled,
                  isCIMBVAEnabled,
                }}
                disabledPayments={disabledPayments}
                donate={onClickPaymentMethod}
              />

              <Flex justifyContent="space-between" marginTop={6}>
                <Flex flexDirection="column">
                  {transactionFeePolicy === 'TIPPER' &&
                    <>
                      <Large fontSize={["md"]}>
                        <Trans>Jumlah Dukungan: </Trans> {formatMoney(values.amount, { currency })}
                      </Large>


                      {(values.platformFee > 0) ? (
                        <Large fontSize={["sm"]}>
                          <Trans>Biaya Layanan: </Trans>{" "}
                          {formatMoney(values.platformFee ? values.platformFee : 0, { currency })}
                        </Large>
                      ) : null}

                      <Large fontSize={["sm"]}>
                        <Trans>Biaya Pembayaran (Payment Gateway):</Trans>
                        {formatMoney(
                          values.pgFee ? values.pgFee : 0,
                          {
                            currency,
                          }
                        )}
                      </Large>
                    </>
                  }
                  <Large>
                    <Trans>Total</Trans> {formatMoney(values.amountToPay, { currency })}
                  </Large>
                </Flex>
                <Flex>
                  <Button
                    marginBottom={0}
                    disabled={!allowPayment || !selectedPaymentMethod}
                    data-testid="donate-button"
                    backgroundColor={`saweria-yellow.${
                      allowPayment && selectedPaymentMethod ? "normal" : "dark"
                    }`}
                    type="button"
                    label={t`Kirim Dukungan`}
                    onClick={submitForm}
                  />
                </Flex>
              </Flex>
            </Form>
          );
        }}
      </Formik>
    </PaymentMethodSelectorProvider>
  );
};

export { SoundboardForm };
